import type { GameQuestion } from '@/types/models';

export interface PresetPack {
  id: string;
  name: string;
  icon: string;
  description: string;
  questions: GameQuestion[];
}

export const PRESET_PACKS: PresetPack[] = [
  {
    id: 'finsteps-workshop-1',
    name: 'FinSteps Workshop 1',
    icon: '💰',
    description: '12 questions across three acts: knowledge recall, street-smart decisions, and real-world application.',
    questions: [
      // ── Act 1: Do You Know Your Money? ────────────────────────────────────
      {
        id: 'fw1-q1',
        type: 'mcq',
        prompt: 'You earn €400/month from a part-time job. Rent is €150, phone is €20, food is €80. How much is left for saving or spending?',
        options: ['€100', '€150', '€170', '€200'],
        correctIndex: 1,
        explanation: '€400 − €150 − €20 − €80 = €150.',
        points: 100,
        timeLimit: 15,
      },
      {
        id: 'fw1-q2',
        type: 'word-bank',
        prompt: 'Fill in the blanks:',
        sentence: "A ___ is a plan for your money. It helps you track your ___ and your ___ so you don't run out before the end of the month.",
        wordBank: ['budget', 'income', 'spending', 'savings', 'debt'],
        correctOrder: [0, 1, 2],
        interchangeable: ['1,2'],
        points: 150,
        timeLimit: 20,
      },
      {
        id: 'fw1-q3',
        type: 'true-false',
        prompt: '',
        statement: "If you save €10 a week for a year, you'll have over €500.",
        isTrue: true,
        explanation: '€10 × 52 weeks = €520. Small amounts add up faster than you think.',
        points: 100,
        timeLimit: 12,
      },
      {
        id: 'fw1-q4',
        type: 'match-pairs',
        prompt: 'Match the term to what it actually means:',
        pairs: [
          { left: 'Budget',   right: 'A plan for how to spend and save your money' },
          { left: 'Interest', right: 'Money a bank pays you for keeping your savings with them' },
          { left: 'Credit',   right: 'Borrowing money now and paying it back later' },
          { left: 'APR',      right: 'The yearly cost of borrowing, shown as a percentage' },
        ],
        points: 200,
        timeLimit: 30,
      },
      {
        id: 'fw1-q5',
        type: 'word-bank',
        prompt: 'Fill in the blanks:',
        sentence: "When a bank offers 3% ___ on your savings, it means your money ___ over time even if you don't add more.",
        wordBank: ['interest', 'grows', 'shrinks', 'debt'],
        correctOrder: [0, 1],
        points: 150,
        timeLimit: 20,
      },
      // ── Act 2: Street Smart ───────────────────────────────────────────────
      {
        id: 'fw1-q6',
        type: 'scenario',
        prompt: "It's Friday night. Your friends are going out for food and it's €25. You have €60 left for the week and still need to buy lunch for 3 school days. What do you do?",
        options: [
          "Go out anyway, you'll figure out lunches later",
          'Suggest somewhere cheaper and keep €30 for lunches',
          'Skip it and save the full €60',
        ],
        pointValues: [0, 100, 0],
        explanations: [
          'Spending €25 leaves you €35 for 3 lunches — tight, but doable. The risk is one unexpected cost tips you over.',
          'Smart compromise. You stay social and keep enough for lunches — this is budgeting in real life.',
          'Financially safe, but skipping everything every week makes saving feel like punishment. Balance matters too.',
        ],
        points: 100,
        timeLimit: 0,
      },
      {
        id: 'fw1-q8',
        type: 'word-bank',
        prompt: 'Fill in the blanks:',
        sentence: 'Buy Now Pay Later means you get the item today but pay in ___. If you miss a payment, you could be charged ___ and end up deeper in ___.',
        wordBank: ['instalments', 'fees', 'debt', 'interest', 'credit'],
        correctOrder: [0, 1, 2],
        points: 150,
        timeLimit: 20,
      },
      {
        id: 'fw1-q9',
        type: 'scenario',
        prompt: 'You want to buy a €300 phone. A site offers "Buy Now, Pay Later" in 6 monthly payments of €55. Your friend says just save up for 3 months instead. What do you do?',
        options: [
          'Use Buy Now Pay Later — I want it now',
          'Save up — I\'ll pay €300 instead of €330',
          'Find a cheaper phone I can afford right now',
        ],
        pointValues: [0, 100, 0],
        explanations: [
          'BNPL costs you 6 × €55 = €330 — €30 more than the phone is worth, plus the risk of fees if you miss a payment.',
          'Saving takes discipline but costs €30 less and leaves no debt. Your friend\'s maths is right.',
          'Practical and debt-free. A cheaper phone now beats an expensive one you\'re still paying off in 6 months.',
        ],
        points: 100,
        timeLimit: 0,
      },
      {
        id: 'fw1-q10',
        type: 'mcq',
        prompt: "You borrow €100 at 20% APR and don't pay anything for a full year. Roughly how much do you owe?",
        options: ['€100', '€110', '€120', '€200'],
        correctIndex: 2,
        explanation: '20% APR on €100 = €20 in interest. Total owed: €120.',
        points: 100,
        timeLimit: 15,
      },
      // ── Act 3: Level Up ───────────────────────────────────────────────────
      {
        id: 'fw1-q11',
        type: 'true-false',
        prompt: '',
        statement: 'A debit card spends your own money. A credit card spends borrowed money.',
        isTrue: true,
        explanation: 'Debit = your money leaves your account immediately. Credit = the bank pays, and you owe them back (with interest if you\'re late).',
        points: 100,
        timeLimit: 12,
      },
      {
        id: 'fw1-q12',
        type: 'true-false',
        prompt: '',
        statement: "When you get paid, the smartest move is to pay your bills and save something before spending on things you want.",
        isTrue: true,
        explanation: "Bills first, savings second, wants last. It's the one habit that separates people who run out of money from people who don't.",
        points: 100,
        timeLimit: 12,
      },
      {
        id: 'fw1-q13',
        type: 'scenario',
        prompt: 'You just got paid €200. You owe a friend €30, your phone bill is €20, and there\'s a €60 jacket you\'ve been wanting for weeks. You also promised yourself you\'d start saving. What do you do first?',
        options: [
          'Buy the jacket before it sells out, sort the rest later',
          'Pay your friend and the phone bill, save €50, then see what\'s left',
          'Save €100 straight away, deal with everything else after',
        ],
        pointValues: [0, 100, 0],
        explanations: [
          'Spending on a want before covering debts and bills is how people end up short at the end of the month.',
          'Obligations first, savings second, wants last — this is the principle from Q12 applied to real life.',
          'Saving €100 is great, but not paying your friend back or your phone bill first creates bigger problems.',
        ],
        points: 100,
        timeLimit: 0,
      },
    ],
  },
  {
    id: 'money-in-60-seconds',
    name: 'Money in 60 Seconds',
    icon: '⏱️',
    description: 'Four quick word-cloud prompts to surface how the room actually thinks about spending, earning and saving.',
    questions: [
      {
        id: 'm60-q1',
        type: 'word-cloud',
        prompt: "What's the last thing you spent money on?",
        maxChars: 150,
        points: 100,
      },
      {
        id: 'm60-q2',
        type: 'word-cloud',
        prompt: 'How do you get your money — pocket money, a job, or something else?',
        maxChars: 150,
        points: 100,
      },
      {
        id: 'm60-q3',
        type: 'word-cloud',
        prompt: 'Do you save money? If yes, what are you saving for?',
        maxChars: 150,
        points: 100,
      },
      {
        id: 'm60-q4',
        type: 'word-cloud',
        prompt: "Most pointless thing you've ever bought?",
        maxChars: 150,
        points: 100,
      },
    ],
  },
  {
    id: 'tweet-your-app',
    name: 'Tweet Your App',
    icon: '🐦',
    description: 'Players draft a mock tweet announcing their own app — name, handle, and 150-char tweet — live on the display.',
    questions: [
      {
        id: 'tw-q1',
        type: 'tweet',
        prompt: "Pitch your app in a single tweet. What's it called, and what's it about?",
        maxChars: 150,
        points: 100,
      },
    ],
  },
  {
    id: 'apps-you-love',
    name: 'Apps You Love',
    icon: '📱',
    description: 'Three quick word-cloud prompts to surface the apps that shape the room\'s day.',
    questions: [
      {
        id: 'apps-q1',
        type: 'word-cloud',
        prompt: "What's the first app you open in the morning?",
        maxChars: 150,
        points: 100,
      },
      {
        id: 'apps-q2',
        type: 'word-cloud',
        prompt: "What's the last app you use before bed?",
        maxChars: 150,
        points: 100,
      },
      {
        id: 'apps-q3',
        type: 'word-cloud',
        prompt: "What's the one app you could never delete? (no social media!)",
        maxChars: 150,
        points: 100,
      },
    ],
  },
];
